import { useCallback, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Trans, useTranslation } from 'react-i18next'

import { useAppLayout } from '@/components/AppLayout/AppLayoutProvider'
import Button from '@/components/Button/Button'
import UIText from '@/components/UI/UIText'
import UITitle from '@/components/UI/UITitle'
import { useAppState } from '@/composables/useAppState'
import { useGSAP } from '@/composables/useGSAP'
import useTracking from '@/composables/useTracking'
import { useTransComponents } from '@/composables/useTransComponents'
import { AppChooseEvent, AppSkipEvent } from '@/state/AppEvent'
import { CompletionStatus } from '@/state/AppStateMachine'

import UIIcon, { UIIconType } from '../UI/UIIcon'

export default function Scenario() {
  const root = useRef<HTMLDivElement>(null)
  const { gsap } = useGSAP()
  const { actor, persona, scenario, state } = useAppState()
  const { footerRight } = useAppLayout()
  const { components } = useTransComponents()
  const { trackImpression, trackClick } = useTracking()
  const { t } = useTranslation()

  const hasCompletedScenario = scenario.status.some((status) => status === CompletionStatus.COMPLETED)

  useEffect(() => {
    trackImpression({
      unitName: `${persona}_simulation`,
      unitVariation: 'choose_scenario',
      unitLocation: 'tyce',
    })
  }, [trackImpression, persona])

  const onNext = useCallback(
    (selectedScenario: number) => {
      actor.send({ type: 'CHOOSE', scenario: selectedScenario } as AppChooseEvent)

      trackClick({
        unitName: `${persona}_simulation_${selectedScenario}`,
        unitVariation: 'choose_scenario',
        unitLocation: 'tyce',

        clickType: 'button',
        buttonText: t(`tutorial.${persona}.${selectedScenario + 1}.intro.cta`),
        buttonName: 'navigation_button',
      })
    },
    [actor, t, persona, trackClick]
  )

  const onSkip = useCallback(() => {
    actor.send({ type: 'SKIP' } as AppSkipEvent)

    trackClick({
      unitName: `${persona}_scenario`,
      unitVariation: 'choose_scenario',
      unitLocation: 'tyce',

      clickType: 'button',
      buttonText: t(`skip`),
      buttonName: 'navigation_button',
    })
  }, [actor, t, persona, trackClick])

  useEffect(() => {
    if (!root.current) return
    gsap.to(root.current, { delay: 0.2, opacity: 1, duration: 0.5, ease: 'power2.out' })
  }, [gsap])

  return (
    <div data-testid='scenario' className='Scenario pt-120 pb-60 md-pb:none z-10 opacity-0' tabIndex={-1} ref={root}>
      <UITitle
        is='h2'
        delay={0.1}
        className='text-center font-normal text-18 md:text-28 mb-20 md:mb-42 leading-none'
        i18nKey={!hasCompletedScenario ? 'tutorial.title' : 'tutorial.completed-title'}
        animate={false}
      />
      <div className='grid grid-cols-12 lg:container px-20 lg:px-0'>
        <div className='col-span-12 lg:col-span-10 lg:col-start-2 xl:col-span-8 xl:col-start-3 grid grid-cols-1 lg:grid-cols-2 gap-52'>
          {scenario.status.map((status, index) => {
            return (
              <div key={index} className='text-center'>
                <UIIcon
                  icon={t(`tutorial.${persona}.${index + 1}.intro.icon`) as UIIconType}
                  className='text-60 md:text-[100px] inline-block'
                />
                <UITitle
                  hasSideDecoration={false}
                  delay={0.3}
                  animate={false}
                  is='h3'
                  className='text-[28px] md:text-[50px] xl:text-[60px] font-semibold leading-none mb-12 md:mb-20'
                  i18nKey={`tutorial.${persona}.${index + 1}.intro.title`}
                />
                <UIText
                  is='p'
                  animate={false}
                  className='text-16 md:text-18 mb-12 md:mb-30'
                  i18nKey={`tutorial.${persona}.${index + 1}.intro.description`}
                />
                <div>
                  {status !== CompletionStatus.COMPLETED && (
                    <Button
                      appear={true}
                      hasParticles={true}
                      onClick={() => onNext(index)}
                      animate={false}
                      ariaLabel={`${t(`tutorial.choose`)} ${t(`tutorial.${persona}.${index + 1}.intro.title`)}`}
                    >
                      <Trans i18nKey={`tutorial.choose`} components={components} />
                    </Button>
                  )}
                  {status === CompletionStatus.COMPLETED && (
                    <Button variant='link' onClick={() => onNext(index)} animate={false}>
                      <div className='flex gap-8 items-center'>
                        <svg width='24' height='23' viewBox='0 0 24 23' fill='none' xmlns='http://www.w3.org/2000/svg'>
                          <path
                            d='M17.7899 8.74002H23.9999V2.53002H21.6999V4.87602C21.3319 4.37002 20.9179 3.91002 20.5039 3.45002C19.4919 2.43802 18.2959 1.61002 16.8699 1.01202C14.2019 -0.183981 11.0739 -0.275981 7.94586 1.01202C6.61186 1.56402 5.36986 2.39202 4.31186 3.45002C3.29986 4.46202 2.47186 5.65802 1.87386 7.08402C1.27586 8.46402 0.953857 9.98202 0.953857 11.546C0.953857 13.018 1.27586 14.49 1.87386 15.962C2.42586 17.296 3.25386 18.538 4.31186 19.596C5.41586 20.7 6.61186 21.528 7.94586 22.034C9.32586 22.632 10.8439 22.954 12.4079 22.954C13.8799 22.954 15.3519 22.632 16.8239 22.034C18.1579 21.482 19.3999 20.654 20.4579 19.596C21.5619 18.492 22.3899 17.296 22.8959 15.962L20.7799 15.088C20.3659 16.1 19.6759 17.112 18.8019 17.986C17.9279 18.86 16.9619 19.504 15.9039 19.964C14.6619 20.47 13.5119 20.7 12.3159 20.7C11.0739 20.7 9.87786 20.47 8.72786 19.964C7.71586 19.55 6.70386 18.86 5.82986 17.986C4.95586 17.112 4.31186 16.146 3.85186 15.088C3.34586 13.846 3.11586 12.696 3.11586 11.5C3.11586 10.258 3.34586 9.06202 3.85186 7.95802C4.35786 6.80802 5.00186 5.84202 5.82986 5.06002C6.70386 4.18602 7.66986 3.54202 8.72786 3.08202C9.96986 2.57602 11.1199 2.34602 12.3159 2.34602C13.5579 2.34602 14.7539 2.57602 15.8579 3.08202C17.0079 3.58802 17.9739 4.23202 18.7559 5.06002C19.1699 5.47402 19.5379 5.93402 19.8599 6.39402H17.6519V8.74002H17.7899Z'
                            fill='#FE9378'
                          />
                        </svg>

                        <Trans i18nKey={`tutorial.start-over`} components={components} />
                      </div>
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {hasCompletedScenario &&
        createPortal(
          <Button appear={true} appearDelay={0.9} hasParticles={true} onClick={() => onSkip()}>
            <Trans i18nKey='tutorial.lets-move-on' components={components} />
          </Button>,
          footerRight.current
        )}
    </div>
  )
}
